import { useState, useEffect, useCallback, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw,
  Download,
  Wifi,
  WifiOff,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  FileDown,
  FileJson,
  CalendarDays,
  Loader2,
  QrCode,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import * as dbOps from '../db/kioskDbOperations';
import { useKioskExport } from '../hooks/useKioskExport';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(isoStr) {
  if (!isoStr) return '--';
  const d = new Date(isoStr);
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(ms) {
  if (!ms || ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function getStatusBadge(status) {
  switch (status) {
    case 'success':
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Success
        </Badge>
      );
    case 'partial':
      return (
        <Badge variant="warning" className="gap-1">
          <AlertTriangle className="w-3 h-3" />
          Partial
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="w-3 h-3" />
          Failed
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function toDateInputValue(date) {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// KioskAdminSync
// ---------------------------------------------------------------------------

export default function KioskAdminSync({ syncEngine }) {
  const [syncLogs, setSyncLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [syncResult, setSyncResult] = useState(null);

  // Export state
  const [exportStart, setExportStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toDateInputValue(d);
  });
  const [exportEnd, setExportEnd] = useState(() => toDateInputValue(new Date()));
  const [exportPreviewCount, setExportPreviewCount] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const previewTimeoutRef = useRef(null);

  // QR viewer state
  const [qrPageIndex, setQrPageIndex] = useState(0);
  const [showQrViewer, setShowQrViewer] = useState(false);

  // Export hook
  const {
    exporting,
    qrData,
    exportToCSV,
    exportToJSON,
    generateQRCodes,
    clearQRData,
  } = useKioskExport();

  // -----------------------------------------------------------------------
  // Load sync logs
  // -----------------------------------------------------------------------
  const loadLogs = useCallback(async () => {
    try {
      const logs = await dbOps.getRecentSyncLogs(10);
      setSyncLogs(logs);
    } catch (err) {
      console.error('Failed to load sync logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // -----------------------------------------------------------------------
  // Sync handler
  // -----------------------------------------------------------------------
  const handleSync = useCallback(async () => {
    setSyncResult(null);
    const result = await syncEngine.sync();
    setSyncResult(result);
    await loadLogs();
  }, [syncEngine, loadLogs]);

  // -----------------------------------------------------------------------
  // Export event count preview - debounced
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    previewTimeoutRef.current = setTimeout(async () => {
      if (!exportStart || !exportEnd) {
        setExportPreviewCount(null);
        return;
      }
      try {
        const events = await dbOps.getEventsByDateRange(exportStart, exportEnd + 'T23:59:59');
        setExportPreviewCount(events.length);
      } catch {
        setExportPreviewCount(null);
      }
    }, 300);

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [exportStart, exportEnd]);

  // -----------------------------------------------------------------------
  // Export handlers
  // -----------------------------------------------------------------------
  const handleExportCSV = useCallback(async () => {
    setExportResult(null);
    const result = await exportToCSV(exportStart, exportEnd);
    setExportResult(result);
  }, [exportStart, exportEnd, exportToCSV]);

  const handleExportJSON = useCallback(async () => {
    setExportResult(null);
    const result = await exportToJSON(exportStart, exportEnd);
    setExportResult(result);
  }, [exportStart, exportEnd, exportToJSON]);

  const handleGenerateQR = useCallback(async () => {
    setQrPageIndex(0);
    await generateQRCodes(exportStart, exportEnd);
    setShowQrViewer(true);
  }, [exportStart, exportEnd, generateQRCodes]);

  const handleCloseQR = useCallback(() => {
    setShowQrViewer(false);
    clearQRData();
  }, [clearQRData]);

  // -----------------------------------------------------------------------
  // Sync progress derived values
  // -----------------------------------------------------------------------
  const progressPercent =
    syncEngine.syncProgress.total > 0
      ? Math.round(
          ((syncEngine.syncProgress.completed + syncEngine.syncProgress.failed) /
            syncEngine.syncProgress.total) *
            100
        )
      : 0;

  const noEvents = exportPreviewCount === 0 || exportPreviewCount === null;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Sync & Export</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage data synchronisation and export events
        </p>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Sync Section                                                      */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-slate-500" />
            Synchronise
          </CardTitle>
          <CardDescription>
            Push pending events to the server. Pending:{' '}
            <span className="font-semibold text-slate-700">{syncEngine.pendingCount}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pt-4 space-y-4">
          {/* Sync button */}
          <Button
            size="lg"
            className="w-full sm:w-auto gap-2"
            disabled={!syncEngine.isOnline || syncEngine.isSyncing}
            onClick={handleSync}
          >
            {syncEngine.isSyncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {syncEngine.isSyncing ? 'Syncing...' : 'Sync Now'}
          </Button>

          {/* Offline notice */}
          {!syncEngine.isOnline && (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              <WifiOff className="w-4 h-4 flex-none" />
              Device is offline. Sync is unavailable until connectivity is restored.
            </div>
          )}

          {/* Progress bar during sync */}
          <AnimatePresence>
            {syncEngine.isSyncing && syncEngine.syncProgress.total > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>Progress</span>
                  <span className="tabular-nums">
                    {syncEngine.syncProgress.completed + syncEngine.syncProgress.failed} /{' '}
                    {syncEngine.syncProgress.total}
                  </span>
                </div>
                <Progress value={progressPercent} className="h-2.5" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Sync result summary */}
          <AnimatePresence>
            {syncResult && !syncEngine.isSyncing && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className={`flex items-start gap-3 p-3 rounded-lg text-sm ${
                  syncResult.success
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'bg-red-50 text-red-800'
                }`}
              >
                {syncResult.success ? (
                  <CheckCircle2 className="w-4 h-4 flex-none mt-0.5" />
                ) : (
                  <XCircle className="w-4 h-4 flex-none mt-0.5" />
                )}
                <div>
                  <p className="font-medium">
                    {syncResult.success ? 'Sync complete' : 'Sync failed'}
                  </p>
                  {syncResult.summary && (
                    <p className="text-xs mt-0.5 opacity-80">
                      {syncResult.summary.synced} synced
                      {syncResult.summary.failed > 0 &&
                        `, ${syncResult.summary.failed} failed`}
                      {syncResult.summary.conflicts > 0 &&
                        `, ${syncResult.summary.conflicts} conflicts`}
                    </p>
                  )}
                  {syncResult.error && (
                    <p className="text-xs mt-0.5 opacity-80">{syncResult.error}</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Auto-sync toggle */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div>
              <Label htmlFor="auto-sync-toggle" className="text-sm font-medium text-slate-700">
                Auto-sync
              </Label>
              <p className="text-xs text-slate-400 mt-0.5">
                Automatically sync every 5 minutes when online
              </p>
            </div>
            <Switch
              id="auto-sync-toggle"
              checked={syncEngine.autoSync}
              onCheckedChange={(checked) => syncEngine.toggleAutoSync(checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Recent Sync History                                               */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            Recent Sync History
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pt-3">
          {logsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : syncLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">
              No sync history yet
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-sm min-w-[500px]">
                <thead>
                  <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    <th className="px-4 py-2">Time</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Synced</th>
                    <th className="px-4 py-2 text-right">Failed</th>
                    <th className="px-4 py-2 text-right">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {syncLogs.map((log, i) => (
                    <tr key={log.id || i} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-2.5">{getStatusBadge(log.status)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                        {log.eventsTotal}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600">
                        {log.eventsSynced}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-red-500">
                        {log.eventsFailed}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-500 whitespace-nowrap">
                        {formatDuration(log.durationMs)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Export Section                                                     */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <FileDown className="w-4 h-4 text-slate-500" />
            Export Events
          </CardTitle>
          <CardDescription>
            Export clock events for a selected date range
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pt-4 space-y-4">
          {/* Date range */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Label htmlFor="export-start" className="text-sm text-slate-600 mb-1.5 block">
                Start date
              </Label>
              <div className="relative">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  id="export-start"
                  type="date"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-colors"
                />
              </div>
            </div>
            <div className="flex-1">
              <Label htmlFor="export-end" className="text-sm text-slate-600 mb-1.5 block">
                End date
              </Label>
              <div className="relative">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  id="export-end"
                  type="date"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Preview count */}
          {exportPreviewCount !== null && (
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-700">{exportPreviewCount}</span>{' '}
              event{exportPreviewCount !== 1 ? 's' : ''} in selected range
            </p>
          )}

          {/* Export result */}
          <AnimatePresence>
            {exportResult && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  exportResult.success
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {exportResult.success ? (
                  <CheckCircle2 className="w-4 h-4 flex-none" />
                ) : (
                  <XCircle className="w-4 h-4 flex-none" />
                )}
                {exportResult.success
                  ? `Exported ${exportResult.shiftCount ?? exportResult.eventCount} records`
                  : `Export failed: ${exportResult.error}`}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Export buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="gap-2 flex-1"
              disabled={exporting || !exportStart || !exportEnd || noEvents}
              onClick={handleExportCSV}
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export CSV
            </Button>
            <Button
              variant="outline"
              className="gap-2 flex-1"
              disabled={exporting || !exportStart || !exportEnd || noEvents}
              onClick={handleExportJSON}
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileJson className="w-4 h-4" />
              )}
              Export JSON
            </Button>
            <Button
              variant="outline"
              className="gap-2 flex-1"
              disabled={exporting || !exportStart || !exportEnd || noEvents}
              onClick={handleGenerateQR}
            >
              <QrCode className="w-4 h-4" />
              Transfer via QR
            </Button>
          </div>

          <p className="text-xs text-slate-400">
            CSV exports paired shift data with durations. JSON includes full event data for server import. QR generates scannable codes for phone-based transfer.
          </p>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* QR Code Viewer Overlay                                            */}
      {/* ----------------------------------------------------------------- */}
      <AnimatePresence>
        {showQrViewer && qrData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              {/* QR Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div>
                  <h3 className="text-base font-semibold text-slate-800">QR Transfer</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {qrData.totalEvents} events in {qrData.totalChunks} QR code{qrData.totalChunks !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={handleCloseQR}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Close QR viewer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* QR Code Display */}
              <div className="p-6 flex flex-col items-center gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <QRCodeSVG
                    value={qrData.chunks[qrPageIndex]}
                    size={256}
                    level="M"
                    includeMargin={true}
                  />
                </div>

                {/* Page indicator */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQrPageIndex((i) => Math.max(0, i - 1))}
                    disabled={qrPageIndex === 0}
                    className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 transition-colors"
                    aria-label="Previous QR code"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-medium text-slate-700 tabular-nums min-w-[60px] text-center">
                    {qrPageIndex + 1} of {qrData.totalChunks}
                  </span>
                  <button
                    onClick={() => setQrPageIndex((i) => Math.min(qrData.totalChunks - 1, i + 1))}
                    disabled={qrPageIndex >= qrData.totalChunks - 1}
                    className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 transition-colors"
                    aria-label="Next QR code"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-xs text-slate-400 text-center">
                  Scan each QR code in order using the companion app or a QR reader to transfer event data
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
